import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ChevronRight, User, Heart, LogOut, Package, MapPin, Star } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { buildLoginHref } from '@/lib/auth';
import { useSiteMenus } from '@/hooks/useSiteMenus';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  categories: { name: string; slug: string; sub_categories: { name: string; slug: string }[] }[];
}

export default function MobileNav({ isOpen, onClose, categories }: MobileNavProps) {
  const { isAuthenticated, customer, logout } = useAuthStore();
  const location = useLocation();
  const loginHref = buildLoginHref(location.pathname, location.search);
  // Admin-managed header links (Content → Site Menus → Header) — shown in a
  // "More" section so the drawer matches the desktop nav bar.
  const headerLinks = useSiteMenus()?.header ?? [];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  return (
    <>
      {/* Overlay. Inline z-index: 1100 beats the sticky .site-header and the
          mobile bottom-nav (both z-index: 1000) so the drawer feels like a
          true app-sheet rather than slipping behind the top bar or tab bar.
          We don't rely on a Tailwind arbitrary z class so this works even if
          the JIT cache misses the value. */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden"
          style={{ zIndex: 1100 }}
          onClick={onClose}
        />
      )}

      {/* Drawer — zIndex 1110 above the overlay. */}
      <div
        className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ zIndex: 1110 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 bg-secondary text-white">
          {isAuthenticated && customer ? (
            <div>
              <p className="font-semibold">{customer.name || 'Welcome!'}</p>
              <p className="text-xs text-white/70">{customer.phone}</p>
            </div>
          ) : (
            <Link to={loginHref} onClick={onClose} className="font-semibold hover:underline">
              Login / Sign Up
            </Link>
          )}
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/20 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav Content */}
        <div className="overflow-y-auto h-[calc(100%-72px)]">
          {/* Categories */}
          <div className="py-2">
            <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Categories
            </p>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/categories/${cat.slug}`}
                onClick={onClose}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-sm text-gray-800">{cat.name}</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            ))}
          </div>

          {headerLinks.length > 0 && (
            <>
              <div className="border-t border-gray-100" />
              <div className="py-2">
                <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  More
                </p>
                {headerLinks.map((link) =>
                  /^https?:\/\//i.test(link.url) || link.new_tab ? (
                    <a
                      key={`cms-${link.label}-${link.url}`}
                      href={link.url}
                      target={link.new_tab ? '_blank' : undefined}
                      rel="noopener"
                      onClick={onClose}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm text-gray-800">{link.label}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </a>
                  ) : (
                    <Link
                      key={`cms-${link.label}-${link.url}`}
                      to={link.url}
                      onClick={onClose}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm text-gray-800">{link.label}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </Link>
                  ),
                )}
              </div>
            </>
          )}

          <div className="border-t border-gray-100" />

          {/* Account Links */}
          {isAuthenticated && (
            <div className="py-2">
              <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                My Account
              </p>
              <NavLink href="/account" icon={<User className="w-4 h-4" />} label="Profile" onClick={onClose} />
              <NavLink href="/orders" icon={<Package className="w-4 h-4" />} label="My Orders" onClick={onClose} />
              <NavLink href="/account/designs" icon={<Heart className="w-4 h-4" />} label="My Designs" onClick={onClose} />
              <NavLink href="/account/addresses" icon={<MapPin className="w-4 h-4" />} label="Addresses" onClick={onClose} />
              <NavLink href="/account/loyalty" icon={<Star className="w-4 h-4" />} label="Loyalty Points" onClick={onClose} />

              <div className="border-t border-gray-100 mt-2" />

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-50 transition-colors text-red-600"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function NavLink({
  href,
  icon,
  label,
  onClick,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Link
      to={href}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
    >
      <span className="text-gray-500">{icon}</span>
      <span className="text-sm text-gray-800">{label}</span>
    </Link>
  );
}
